'use client'

import type { FunctionReturnType } from 'convex/server'
import { useRouter } from 'next/navigation'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react'
import { useOptionalOrgContext } from '@/contexts/org-context'
import {
  isSelectedBrandValid,
  resolveActiveBrandId,
  resolveIsActiveBrandReady,
  resolveTrustedSeedId,
} from '@/contexts/resolve-active-brand-id'
import { api } from '@/convex/_generated/api'
import { useSoftAuthedQuery } from '@/hooks/use-authed-query'
import { isConvexOrgReady, useConvexOrgId } from '@/hooks/use-convex-org-id'
import { switchBrand } from '@/lib/actions/brand-actions'
import { forgetAudienceContactsReads } from '@/lib/audience/contacts-cache-generation'
import {
  filterAccessibleBrands,
  resolveBrandRoleFromSnapshot,
} from '@/lib/auth/org-access'
import {
  clearBrandIntentCookie,
  readBrandIntentCookie,
} from '@/lib/brand/home/setup-wizard-cookie'
import { appToast } from '@/lib/shared/toast'
import { withAssetCacheBust } from '@/lib/shared/utils/asset-cache-bust'
import { getFaviconUrl } from '@/lib/shared/utils/favicon'

type BrandLogo = {
  type: string
  theme: 'light' | 'dark'
  src: string
  /** The brand's chosen logo (Brand page → Logo); see `BrandLogo.isPrimary`. */
  isPrimary?: boolean
}

type BrandOverviewLike = {
  brandName?: string
  favicon?: string
}

type BrandLogoRowLike = {
  src?: string
  url?: string
  type?: string
  theme?: string
  variant?: string
  isPrimary?: boolean
}

type BrandIdentityLike = {
  brandName?: string
}

/**
 * Raw row shape returned by `api.brands.queries.listOrgBrands`. The switcher
 * projection sends `brandName`, logos, and `colorSwatches`. `identity` and
 * `emailDesignMd` remain only for an older payload from the previous deploy.
 */
type RawBrandRow = {
  _id: string
  _creationTime: number
  updatedAt?: number
  brandName?: string
  domain: string
  favicon?: string
  logos?: Array<BrandLogoRowLike>
  identity?: BrandIdentityLike
  overview?: BrandOverviewLike
  status: string
  /** Pre-extracted hexes from `listOrgBrands`. Absent on an older payload. */
  colorSwatches?: Array<string>
  /**
   * Present only while an older deployment still returns the markdown on
   * the switcher list. New payloads send `colorSwatches` instead.
   */
  emailDesignMd?: string
}

export type BrandDoc = {
  _id: string
  brandName?: string
  domain: string
  favicon?: string
  logos?: Array<BrandLogo>
  status: string
  /**
   * Hex swatches from the switcher list. `useActiveBrandColors` reads these
   * so the shell does not subscribe to `emailDesignMd`.
   */
  colorSwatches?: Array<string>
  /**
   * Older switcher payloads still carry the markdown. The color hook parses
   * it only when `colorSwatches` is absent.
   */
  emailDesignMd?: string
}

function deriveFavicon(domain: string): string | undefined {
  return getFaviconUrl(domain) ?? undefined
}

function toBrandDoc(b: RawBrandRow): BrandDoc {
  // Version brew-CDN asset URLs by the brand's last-modified time. Logos
  // are uploaded to stable, overwritten-in-place paths, so a re-extraction
  // swaps the bytes without changing the URL — leaving Next.js's image
  // optimizer serving its (up to a year) cached optimization, which can be
  // a stale or even another brand's logo. `patchBrand` bumps `updatedAt` on
  // every logo write, so keying the URL on it re-optimizes the CURRENT
  // source whenever the brand changes. See `withAssetCacheBust`.
  //
  // `updatedAt` bumps on ANY brand patch (rename, theme, image adds) — not
  // only logo writes — so the icon occasionally re-optimizes identical
  // bytes. That over-invalidation is deliberate and cheap: busting too often
  // is strictly safer than a stale/mis-poisoned logo. A hash of `logos`
  // wouldn't tighten it — the `src` strings are STABLE across re-extractions
  // (that's the whole bug); only a dedicated logo-write timestamp would, and
  // that isn't worth a schema change here.
  const version = b.updatedAt ?? b._creationTime
  const logos = (b.logos ?? [])
    .map((l) => {
      let src = ''
      if (typeof l?.src === 'string' && l.src.length > 0) {
        src = l.src
      } else if (typeof l?.url === 'string' && l.url.length > 0) {
        src = l.url
      }
      if (!src) {
        return null
      }
      let theme: 'light' | 'dark' = 'dark'
      if (l.theme === 'light' || l.theme === 'dark') {
        theme = l.theme
      } else if (l.variant === 'light') {
        theme = 'light'
      }
      return {
        type: typeof l.type === 'string' && l.type.length > 0 ? l.type : 'logo',
        theme,
        src: withAssetCacheBust(src, version) ?? src,
        ...(l.isPrimary === true ? { isPrimary: true } : {}),
      }
    })
    .filter((l): l is BrandLogo => l !== null)
  return {
    _id: b._id,
    brandName: b.brandName ?? b.identity?.brandName ?? b.overview?.brandName,
    domain: b.domain,
    favicon: withAssetCacheBust(
      b.overview?.favicon ?? b.favicon ?? deriveFavicon(b.domain),
      version
    ),
    logos,
    status: b.status,
    ...(Array.isArray(b.colorSwatches)
      ? {
          colorSwatches: b.colorSwatches.filter(
            (swatch): swatch is string => typeof swatch === 'string'
          ),
        }
      : {}),
    ...(typeof b.emailDesignMd === 'string'
      ? { emailDesignMd: b.emailDesignMd }
      : {}),
  }
}

type BrandContextValue = {
  brandId: string
  brand: BrandDoc | null
  brands: Array<BrandDoc>
  isLoading: boolean
  setBrandId: (id: string) => void
  isSwitching: boolean
  /**
   * True when the org has AT LEAST ONE fully extracted brand. Drives the
   * brand-setup gate / wizard routing and the composer COPY (first-brand
   * setup vs. an in-flight brand merely being selected) — never composer
   * enablement, which keys on `isActiveBrandReady`.
   *
   * Distinct from `BrandSetupContextValue.isReady` (the brand-setup
   * context below, provided by `app/(app)/_brand-gate/brand-gate-client.tsx`),
   * which only reflects the MOST RECENT brand's status — used to drive the
   * extraction dialog/indicator, not chat eligibility.
   */
  hasReadyBrand: boolean
  /**
   * True when the ACTIVE brand (`brandId`) is fully extracted. The chat
   * composers gate submission on this: a new chat is scoped to the active
   * brand, which may legitimately be `extracting` — the app scopes to a
   * brand from the moment its domain is submitted and every other surface
   * renders against it while the extraction chat fills it in. Seeded from
   * the server while `listOrgBrands` loads; see `resolveIsActiveBrandReady`.
   */
  isActiveBrandReady: boolean
}

const BrandContext = createContext<BrandContextValue | null>(null)

export type { BrandContextValue }
/**
 * Raw context export for fixture surfaces (`/test/email-canvas`) that mount
 * a literal, auth-free brand value. Product code always goes through
 * `BrandContextProvider`, which owns seeding/validation/switching.
 */
export { BrandContext }

export function BrandContextProvider({
  initialBrandId,
  initialBrandIdValidated = false,
  initialHasReadyBrand = false,
  initialActiveBrandReady = false,
  children,
}: {
  initialBrandId?: string
  /**
   * True when the server positively validated `initialBrandId` against this
   * request's Convex brand reads (`resolveInitialBrand`). Lets the seed be
   * trusted while `listOrgBrands` is still loading so brand-scoped
   * subscriptions fire on first paint. An unvalidated cookie-echo seed keeps
   * the conservative hold-until-loaded behavior.
   */
  initialBrandIdValidated?: boolean
  /**
   * Server truth for "the org has ≥1 ready brand" (from the shell's
   * brand-gate snapshot), honored only while `listOrgBrands` loads — backs
   * the brand gate and the composer copy from the first paint.
   */
  initialHasReadyBrand?: boolean
  /**
   * Server truth for "the seeded active brand is ready" (the shell computes
   * it from its ready-brands read + the seeded brand id), honored only while
   * `listOrgBrands` loads — keeps the /home composer enabled from the first
   * paint instead of sitting disabled for the whoami + list round-trip.
   */
  initialActiveBrandReady?: boolean
  children: ReactNode
}) {
  const org = useOptionalOrgContext()
  const orgId = org?.orgId
  // Seeded grants (kept live by the org context). The server list is already
  // narrowed to accessible brands; this repeats the filter on the client so a
  // bundle running against the previous function set stays correct.
  const orgAccess = org?.orgAccess
  // The org id carried by the live Convex JWT. During a Clerk org switch
  // the server-seeded `orgId` updates before the JWT settles; firing
  // `listOrgBrands` with the not-yet-authorized id throws
  // `NOT_AUTHORIZED`. Hold the query until the JWT claim matches.
  const convexOrgId = useConvexOrgId()
  const router = useRouter()
  const normalizedInitialBrandId = initialBrandId || undefined
  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(
    normalizedInitialBrandId
  )
  const [isSwitching, startTransition] = useTransition()

  // Keep the React state aligned with the cookie-derived `initialBrandId`
  // from the server. Without this sync, a fresh extraction can update the
  // cookie + RSC payload to the new brand while the React state stays
  // pinned to the brand that was active at mount time, leaving the user
  // "stuck on the previous brand" everywhere `useBrandContext()` is read.
  //
  // Updating state during render (gated by a `prevInitialBrandId` ref of
  // the last-seen prop) is the supported React pattern for adjusting
  // state in response to a prop change without an effect — React
  // short-circuits the render and re-runs with the new state.
  const [lastSyncedInitialBrandId, setLastSyncedInitialBrandId] = useState<
    string | undefined
  >(normalizedInitialBrandId)
  if (lastSyncedInitialBrandId !== normalizedInitialBrandId) {
    setLastSyncedInitialBrandId(normalizedInitialBrandId)
    if (normalizedInitialBrandId) {
      setSelectedBrandId(normalizedInitialBrandId)
    }
  }

  const brandsQuery = useSoftAuthedQuery(
    api.brands.queries.listOrgBrands,
    isConvexOrgReady(convexOrgId, orgId) && orgId ? { orgId } : 'skip'
  )
  const brandsResult = brandsQuery.data
  // The Convex query already filters out `'deleting'` rows, but we
  // defensively repeat the filter on the client so the UI never shows
  // a brand that's mid-deletion even if the server-side filter is
  // somehow bypassed.
  const brands: Array<BrandDoc> = useMemo(() => {
    const live = (brandsResult ?? []).filter(
      (b: RawBrandRow) => b.status !== 'deleting'
    )
    const accessible = orgAccess
      ? filterAccessibleBrands(live, orgAccess)
      : live
    return accessible.map((b: RawBrandRow) => toBrandDoc(b))
  }, [brandsResult, orgAccess])

  const completedBrands = useMemo(
    () => brands.filter((b) => b.status === 'ready'),
    [brands]
  )
  const areBrandsLoaded = brandsResult !== undefined || brandsQuery.isError
  // Seed `hasReadyBrand` from the server's ready-brands read while
  // `listOrgBrands` is still loading so the brand gate and the composer
  // copy are right from the first paint. The loaded list stays
  // authoritative (a brand deleted mid-flight flips this false as soon as
  // it lands).
  const hasReadyBrand = areBrandsLoaded
    ? completedBrands.length > 0
    : initialHasReadyBrand === true
  // Only promote the selected/seeded id once `listOrgBrands` has loaded AND
  // confirms it — EXCEPT a server-VALIDATED seed (`initialBrandIdValidated`),
  // which is trusted during the loading window so brand-scoped subscriptions
  // fire on first paint. An unvalidated seed can be a raw cookie echo (incl.
  // a legacy `customBrands` id from another id-space), and feeding that to a
  // `v.id('brands')` query throws an uncaught ArgumentValidationError during
  // the org-switch window — those keep yielding `''` (skip) until the list
  // lands. `resolveTrustedSeedId` additionally withholds the trust while the
  // live Convex JWT positively mismatches the server org (the residual
  // org-switch window), so brand-scoped queries never fire the new org's
  // args against the previous org's token. See `isSelectedBrandValid`.
  const resolvedId = isSelectedBrandValid({
    selectedBrandId,
    areBrandsLoaded,
    brands,
    trustedSeedId: resolveTrustedSeedId({
      initialBrandId: normalizedInitialBrandId,
      isInitialBrandIdValidated:
        initialBrandIdValidated &&
        !brandsQuery.isError &&
        (!orgAccess ||
          resolveBrandRoleFromSnapshot(
            orgAccess,
            normalizedInitialBrandId ?? null
          ) !== null),
      convexOrgId,
      orgId,
    }),
  })
    ? selectedBrandId
    : undefined

  // Resolve the active brand id: just-added (intent) extracting brand →
  // explicit selection → newest ready brand → (first-brand only) the seeded
  // extracting brand → '' while loading. See `resolveActiveBrandId` for the
  // full decision table. The intent cookie is read live (not a prop) so an
  // in-session "Add Brand" flips the sidebar the moment the new row arrives via
  // `listOrgBrands`, without waiting for an RSC re-seed. The cookie is then
  // persisted lazily below so subsequent page loads have it pre-set.
  const brandId = resolveActiveBrandId({
    resolvedId,
    areBrandsLoaded,
    brands,
    intentBrandId: orgId ? readBrandIntentCookie(orgId) : null,
  })

  const persistedFallbacksRef = useRef(new Set<string>())
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Ultracite baseline — verify deps when touching this hook
  useEffect(() => {
    if (!orgId || !brandId || resolvedId) {
      return
    }
    const persistenceKey = `${orgId}:${brandId}`
    if (persistedFallbacksRef.current.has(persistenceKey)) {
      return
    }
    // A legacy intent brand is persisted by the brand gate on its
    // `extracting → ready` transition. Skip the redundant write here so the
    // one-shot intent remains owned by one effect during rollout.
    if (brandId === readBrandIntentCookie(orgId)) {
      return
    }
    persistedFallbacksRef.current.add(persistenceKey)
    startTransition(() => {
      void switchBrand(brandId).then((result) => {
        if (!result.success) {
          persistedFallbacksRef.current.delete(persistenceKey)
        }
      })
    })
  }, [brandId, orgId, resolvedId, startTransition])

  const brand = useMemo(
    () => brands.find((b) => b._id === brandId) ?? null,
    [brands, brandId]
  )

  // The composer gate. Seeded from the server (`initialActiveBrandReady`)
  // while the list loads — without the seed the /home composer painted
  // instantly but sat DISABLED for the whoami + list round-trip on every
  // cold load — then the live status of the ACTIVE brand wins.
  const isActiveBrandReady = resolveIsActiveBrandReady({
    areBrandsLoaded,
    activeBrandStatus: brand?.status,
    initialActiveBrandReady,
  })

  const setBrandId = useCallback(
    (id: string) => {
      const previousBrandId = brandId || selectedBrandId
      // A manual pick cancels any pending auto-switch, so a brand finishing in
      // the background can't yank the user off the brand they just chose.
      if (orgId) {
        clearBrandIntentCookie(orgId)
      }
      // Contacts reads belong to the brand being left; the user lands on
      // /home, so no grid of the new brand is reading yet.
      if (id !== previousBrandId) {
        forgetAudienceContactsReads()
      }
      // Flip the active brand AND return to /home in a SINGLE transition. A
      // user-initiated switch always lands on /home: open tabs, the tab strip,
      // and the active chat are scoped per (orgId, brandId), and the chat
      // surfaces also encode the *previous* brand's chat in the URL
      // (`/chat/<id>` full page, `?chat=` split) plus stale `?emailIds=` /
      // `?automationIds=` / `?groupIds=` focus markers; landing on /home (an
      // excluded chat path, no params) drops that surface so the previous
      // brand's conversation can neither render nor replay against the new
      // brand's data.
      //
      // Doing the flip + nav TOGETHER inside `startTransition` is what kills the
      // flash: React keeps the CURRENT page (old brand) painted until /home for
      // the NEW brand is ready, so a brand-scoped route like /emails never
      // repaints against the new brand for a frame on the way out. The flip MUST
      // stay synchronous inside the transition — flipping it outside a
      // transition (the old bug) OR awaiting `switchBrand` before it both make
      // the old route paint the new brand first (verified empirically).
      startTransition(() => {
        setSelectedBrandId(id)
        router.replace('/home')
      })
      // Persist the org-scoped active-brand cookie (httpOnly — only a Server
      // Action can set it) and revalidate brand-scoped RSC so SSR, reloads, and
      // later navigations resolve the new brand. Kept in its OWN transition: its
      // post-`await` work would otherwise escape the flip+nav transition above
      // and reintroduce the flash.
      startTransition(async () => {
        const result = await switchBrand(id)
        if (result.success) {
          router.refresh()
          return
        }
        setSelectedBrandId(previousBrandId)
        appToast.error('Could not switch brands. Please try again.')
      })
    },
    [brandId, orgId, router, selectedBrandId]
  )

  const isLoading = brandsResult === undefined && !brandsQuery.isError

  const value = useMemo<BrandContextValue>(
    () => ({
      brandId,
      brand,
      brands,
      isLoading,
      setBrandId,
      isSwitching,
      hasReadyBrand,
      isActiveBrandReady,
    }),
    [
      brandId,
      brand,
      brands,
      isLoading,
      setBrandId,
      isSwitching,
      hasReadyBrand,
      isActiveBrandReady,
    ]
  )

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export function useBrandContext(): BrandContextValue {
  const ctx = useContext(BrandContext)
  if (!ctx) {
    throw new Error(
      'useBrandContext must be used within a BrandContextProvider'
    )
  }
  return ctx
}

export function useOptionalBrandContext(): BrandContextValue | null {
  return useContext(BrandContext)
}

// ---------------------------------------------------------------------------
// Brand-setup gate context (folded in from app/(app)/_brand-gate, #1245).
//
// A SEPARATE provider on purpose (decision B-5): `BrandGateClient` mounts it
// inside `BrandContextProvider` with the org's live setup status, and its
// value changes on every extraction-progress tick. Keeping it out of
// `BrandContextValue` means those ticks re-render only the setup consumers
// (the switcher's "Add brand", the /brand extracting strip, the wizard) and
// never the ~100 `useBrandContext` readers.
// ---------------------------------------------------------------------------

/**
 * Live brand-setup status, mirroring the discriminated-union return of
 * `api.brands.queries.getActiveBrandStatusForOrg` (agent-native table).
 * Re-exported here so client components can pattern match without importing
 * Convex types directly.
 */
export type BrandSetupStatus = FunctionReturnType<
  typeof api.brands.queries.getActiveBrandStatusForOrg
>

export type BrandSetupContextValue = {
  status: BrandSetupStatus
  /**
   * True while the client query path is waiting on
   * `getActiveBrandStatusForOrg` (preload failed or auth bootstrap).
   * Consumers should trust server-rendered hints until this clears.
   */
  isStatusLoading: boolean
  /** True when the org has at least one fully extracted brand (server snapshot until client list loads). */
  hasReadyBrand: boolean
  /** No real (non-default) brand row exists yet for the org. */
  isNone: boolean
  /** Extraction is in progress for the most recent brand. */
  isExtracting: boolean
  /** The most recent extraction failed and is awaiting a retry. */
  isFailed: boolean
  /** The org has at least one fully extracted brand. */
  isReady: boolean
  /**
   * Navigate to a fresh, session-scoped `/setup?mode=add` flow — used by "Add
   * brand" affordances when the org already has a ready brand.
   */
  requestBrandSetup: () => void
}

const BrandSetupContext = createContext<BrandSetupContextValue | null>(null)

export function BrandSetupProvider({
  value,
  children,
}: {
  value: BrandSetupContextValue
  children: ReactNode
}) {
  return (
    <BrandSetupContext.Provider value={value}>
      {children}
    </BrandSetupContext.Provider>
  )
}

/**
 * Subscribe to the brand-setup gate state from anywhere inside the
 * authed app layout. Returns null when called outside the gate's
 * provider so non-gated surfaces (auth pages, public composer) stay
 * unaffected.
 */
export function useOptionalBrandSetup(): BrandSetupContextValue | null {
  return useContext(BrandSetupContext)
}
